import {
  Banknote,
  Cpu,
  HeartPulse,
  Zap,
  Factory,
  BriefcaseBusiness,
  ShoppingBag,
  RadioTower,
  TrainFront,
  Scale,
  Building2,
  Landmark,
} from 'lucide-react';
import type { Industry } from '../types/content';

export const industries: Industry[] = [
  {
    slug: 'financial-services',
    name: 'Financial Services & Banking',
    icon: Banknote,
    intro: 'Leadership for banks, asset managers, insurers and fintechs operating under close regulatory scrutiny.',
    roles: ['Chief Financial Officer', 'Chief Risk Officer', 'Head of Compliance', 'Managing Director, Corporate Banking'],
    challenges: ['Regulatory approval of senior appointments', 'Competition for risk and compliance leaders', 'Balancing digital and traditional banking expertise'],
    services: ['c-suite-leadership', 'board-non-executive', 'specialist-professional'],
  },
  {
    slug: 'technology',
    name: 'Technology & Digital Transformation',
    icon: Cpu,
    intro: 'Product, engineering and transformation leaders for technology companies and organisations modernising at scale.',
    roles: ['Chief Technology Officer', 'Chief Digital Officer', 'VP Engineering', 'Head of Data & AI'],
    challenges: ['Leaders who combine technical depth and commercial judgement', 'Fast-moving remuneration expectations', 'Assessing transformation track records'],
    services: ['executive-search', 'senior-management', 'interim-contract'],
  },
  {
    slug: 'healthcare',
    name: 'Healthcare & Life Sciences',
    icon: HeartPulse,
    intro: 'Clinical, scientific and commercial leadership across providers, pharmaceuticals, medtech and research.',
    roles: ['Medical Director', 'Head of Regulatory Affairs', 'Chief Operating Officer', 'Non-Executive Director'],
    challenges: ['Scarce clinical-commercial hybrid profiles', 'Regulatory and quality expertise', 'Mission-driven cultural alignment'],
    services: ['board-non-executive', 'specialist-professional', 'executive-search'],
  },
  {
    slug: 'energy',
    name: 'Energy & Utilities',
    icon: Zap,
    intro: 'Leaders navigating energy transition, infrastructure investment and regulated utility operations.',
    roles: ['Managing Director', 'Head of Regulatory Affairs', 'Director of Renewables', 'Chief Operating Officer'],
    challenges: ['Transition skills in traditional organisations', 'Regulatory and stakeholder complexity', 'Safety-critical leadership'],
    services: ['executive-search', 'talent-mapping', 'senior-management'],
  },
  {
    slug: 'manufacturing',
    name: 'Manufacturing & Industrial Operations',
    icon: Factory,
    intro: 'Operational and commercial leadership for manufacturers, engineering firms and supply-chain businesses.',
    roles: ['Operations Director', 'Group HR Director', 'Supply Chain Director', 'Plant General Manager'],
    challenges: ['Operational excellence alongside digitalisation', 'Multi-site leadership experience', 'Succession for long-tenured leaders'],
    services: ['interim-contract', 'senior-management', 'talent-advisory'],
  },
  {
    slug: 'professional-services',
    name: 'Professional & Business Services',
    icon: BriefcaseBusiness,
    intro: 'Partners, practice leaders and operational executives for advisory, consulting and business-services firms.',
    roles: ['Practice Leader', 'Chief Operating Officer', 'Data Privacy Manager', 'Head of Business Development'],
    challenges: ['Portable client relationships', 'Partnership culture and economics', 'Specialist advisory expertise'],
    services: ['specialist-professional', 'permanent-recruitment', 'confidential-retained'],
  },
  {
    slug: 'consumer-retail',
    name: 'Consumer Goods & Retail',
    icon: ShoppingBag,
    intro: 'Commercial, brand and operational leaders for consumer businesses adapting to changing customer behaviour.',
    roles: ['Chief Operating Officer', 'Commercial Director', 'Head of E-commerce', 'Brand Director'],
    challenges: ['Omnichannel and digital commerce experience', 'Margin pressure and operational efficiency', 'Consumer-centric leadership'],
    services: ['c-suite-leadership', 'senior-management', 'talent-mapping'],
  },
  {
    slug: 'telecoms-media',
    name: 'Telecommunications & Media',
    icon: RadioTower,
    intro: 'Leadership for network operators, digital media and content businesses in fast-converging markets.',
    roles: ['Director of Digital Transformation', 'Chief Commercial Officer', 'Head of Network Strategy', 'Content Director'],
    challenges: ['Convergence of technology and content', 'Regulatory and spectrum expertise', 'Transformation in legacy organisations'],
    services: ['executive-search', 'interim-contract', 'talent-advisory'],
  },
  {
    slug: 'infrastructure',
    name: 'Infrastructure & Transportation',
    icon: TrainFront,
    intro: 'Leaders who deliver major programmes and operate essential transport and infrastructure assets.',
    roles: ['Operations Director', 'Programme Director', 'Chief Engineer', 'Commercial Director'],
    challenges: ['Major-programme delivery experience', 'Public and private stakeholder management', 'Safety and regulatory accountability'],
    services: ['senior-management', 'interim-contract', 'executive-search'],
  },
  {
    slug: 'legal-risk',
    name: 'Legal, Risk & Corporate Governance',
    icon: Scale,
    intro: 'General counsel, company secretaries and risk leaders who protect and enable the organisation.',
    roles: ['General Counsel', 'Company Secretary', 'Chief Risk Officer', 'Head of Internal Audit'],
    challenges: ['Commercially minded legal leadership', 'Governance expertise for listed companies', 'Independence and integrity'],
    services: ['c-suite-leadership', 'board-non-executive', 'confidential-retained'],
  },
  {
    slug: 'real-estate',
    name: 'Real Estate & Construction',
    icon: Building2,
    intro: 'Development, investment and delivery leaders across real estate and the built environment.',
    roles: ['Senior Development Manager', 'Head of Asset Management', 'Construction Director', 'Investment Director'],
    challenges: ['Cyclical market conditions', 'Sustainability and ESG expertise', 'Project and investment track records'],
    services: ['permanent-recruitment', 'specialist-professional', 'talent-mapping'],
  },
  {
    slug: 'government',
    name: 'Government Affairs & Regulated Industries',
    icon: Landmark,
    intro: 'Public affairs, policy and regulatory leaders for organisations working closely with government.',
    roles: ['Head of Government Affairs', 'Director of Public Policy', 'Regulatory Director', 'Head of Corporate Affairs'],
    challenges: ['Political neutrality and discretion', 'Cross-sector policy credibility', 'Transitions between public and private sectors'],
    services: ['senior-management', 'confidential-retained', 'talent-advisory'],
  },
];
